import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Icon } from '@iq/react-components';
import ListItem from '../../../../ListItem/ListItem';
import TypeLabel from '../../../../TypeLabel';
import ConfirmationDialog from '../../../../ConfirmationDialog/ConfirmationDialog';
import LabelWithTooltip from '../../../../LabelWithTooltip/LabelWithTooltip';
import EditModal from '../EditModal';

import { getActiveSite } from '../../../../../bundles/sites';
import {
  updateNotificationConfig,
  createNotificationConfig,
  setCopiedSettings,
  getCopiedSettings,
} from '../../../../../bundles/notification-config';
import { isConfigCustom, getRoleDefaults } from '../../constants';
import { ExpansionRow, useExpandedSet, buildCopyAction, buildPasteAction } from '../shared';

const COLUMN_WIDTHS = { type: 'rem', widths: [4, 16, 6, 18, 'fill'] };

const SPACERS = {
  caret: '4rem',
  role: '16rem',
  onoff: '6rem',
  delivery: 'calc(100% - 51rem)',
  options: '7rem',
};

const EXPANSION_SPACERS = {
  before: [
    [SPACERS.caret, 'role-expansion__col-caret'],
    [SPACERS.role, 'role-expansion__col-role'],
  ],
  onoffWidth: SPACERS.onoff,
  deliveryWidth: SPACERS.delivery,
  after: [[SPACERS.options, 'role-expansion__col-options']],
};

const RoleList = ({ roles }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const copiedSettings = useSelector(getCopiedSettings);
  const {
    expandedIds: expandedRoles,
    allExpanded,
    toggleAll,
    toggleOne: toggleRole,
  } = useExpandedSet(roles);
  const [editingRole, setEditingRole] = useState(null);
  const [pasteTarget, setPasteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const handleSave = (role, data) => {
    if (role.config) {
      dispatch(updateNotificationConfig(role.config.id, data));
    } else {
      dispatch(
        createNotificationConfig({
          eventType: 'dtmp',
          role: role.id,
          org: site.org,
          site: site.id,
          ...data,
        })
      );
    }
    setEditingRole(null);
  };

  const handleCopy = (role) => {
    if (role.config) {
      dispatch(setCopiedSettings(role.type, role.config.stateChangeSettings));
    }
  };

  const handlePasteConfirm = () => {
    if (pasteTarget && copiedSettings) {
      handleSave(pasteTarget, {
        stateChangeSettings: { ...copiedSettings.stateChangeSettings },
      });
    }
    setPasteTarget(null);
  };

  const handleResetConfirm = () => {
    if (resetTarget) {
      const defaults = getRoleDefaults(resetTarget.type);
      handleSave(resetTarget, {
        stateChangeSettings: { ...defaults.stateChangeSettings },
      });
    }
    setResetTarget(null);
  };

  const getMenuActions = (role) => {
    const currentSettings =
      role.config?.stateChangeSettings || getRoleDefaults(role.type).stateChangeSettings;

    const actions = [
      { text: 'Edit', action: () => setEditingRole(role), placement: 'before' },
      buildCopyAction(() => handleCopy(role), 'Copy notification settings from this role.'),
      buildPasteAction({
        copiedSettings,
        currentSettings,
        onPaste: () => setPasteTarget(role),
      }),
    ];
    if (isConfigCustom(role.type, role.config)) {
      actions.push({
        text: 'Reset to default',
        action: () => setResetTarget(role),
        placement: 'before',
      });
    }
    return actions;
  };

  const headerColumns = useMemo(
    () => [
      <div
        key="caret"
        className="role-caret-cell"
        onClick={toggleAll}
      >
        <Icon
          icon={allExpanded ? 'he-up' : 'he-down'}
          size="xs"
        />
      </div>,
      <div key="role">Role</div>,
      <div key="onoff">On/Off</div>,
      <div key="conditions">Conditions</div>,
      <div key="delivery">Delivery Options</div>,
    ],
    [allExpanded, toggleAll]
  );

  if (roles.length === 0) {
    return <div className="role-list__empty">No roles found.</div>;
  }

  return (
    <div className="role-list">
      <ListItem
        isHeader
        columns={headerColumns}
        columnWidths={COLUMN_WIDTHS}
        withActionHeader
      />

      {roles.map((role, index) => {
        const custom = isConfigCustom(role.type, role.config);
        const expanded = expandedRoles.has(role.id);
        const zebraClass = index % 2 === 0 ? 'role-row--odd' : 'role-row--even';

        const columns = [
          <div
            key="caret"
            className="role-caret-cell"
          >
            <Icon
              icon={expanded ? 'he-up' : 'he-down'}
              size="xs"
            />
          </div>,
          <div
            key="role"
            className="role-name-content"
          >
            <LabelWithTooltip name={role.type} />
            <div className="role-name-content__tag">
              {custom && (
                <TypeLabel
                  text="Custom"
                  color="grey"
                />
              )}
            </div>
          </div>,
          <div key="onoff" />,
          <div
            key="conditions"
            className="role-row__conditions-label"
          >
            {expanded && 'Notify when alarm reaches status:'}
          </div>,
          <div key="delivery" />,
        ];

        return (
          <React.Fragment key={role.id}>
            <div
              className={`role-row-wrapper ${zebraClass}`}
              onClick={(event) => {
                if (event.target.closest('.list-item-menu, .dropdown-menu')) return;
                toggleRole(role.id);
              }}
            >
              <ListItem
                item={role}
                itemIndex={index}
                columns={columns}
                columnWidths={COLUMN_WIDTHS}
                disableDefaultActions
                extraMenuActions={getMenuActions(role)}
              />
            </div>
            <div
              className={`role-expansion-wrapper ${zebraClass}${expanded ? ' role-expansion-wrapper--open' : ''}`}
            >
              <div className={`role-expansion ${zebraClass}`}>
                <ExpansionRow
                  settings={
                    role.config?.stateChangeSettings ||
                    getRoleDefaults(role.type).stateChangeSettings
                  }
                  prefix="role-expansion"
                  spacers={EXPANSION_SPACERS}
                />
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {editingRole && (
        <EditModal
          config={editingRole.config}
          roleName={editingRole.type}
          onClose={() => setEditingRole(null)}
          onSave={(data) => handleSave(editingRole, data)}
        />
      )}

      {pasteTarget && copiedSettings && (
        <ConfirmationDialog
          title="Paste notification settings"
          body={
            <>
              <p>
                You are pasting settings from <strong>{copiedSettings.sourceRole}</strong> to{' '}
                <strong>{pasteTarget.type}</strong>. This will overwrite the current settings for
                the target role.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Are you sure you want to replace the current settings?
              </p>
            </>
          }
          confirmText="Paste"
          confirmType="primary"
          showCancel
          onConfirm={handlePasteConfirm}
          onCancel={() => setPasteTarget(null)}
          className="size-s"
        />
      )}

      {resetTarget && (
        <ConfirmationDialog
          title="Reset role to default"
          body={
            <>
              <p>
                This action will revert all settings for <strong>{resetTarget.type}</strong> to
                their original defaults. All customizations will be lost.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Are you sure you want to reset this role to default?
              </p>
            </>
          }
          confirmText="Reset"
          confirmType="danger"
          showCancel
          onConfirm={handleResetConfirm}
          onCancel={() => setResetTarget(null)}
          className="size-s"
        />
      )}
    </div>
  );
};

export default RoleList;
